/**
 * Tablet console capture for Alpha diagnostics (#718).
 *
 * Wraps `window.console.{log, info, warn, error}` so every call also
 * lands in an in-memory ring buffer. Also catches `window.error` and
 * `unhandledrejection`. At end-of-call, voice.js (TabletApp.js)
 * flushes the buffer over the active voice WS as one
 * `{type: 'client_console_logs', entries: [...], truncated: bool}`
 * message — see app/api/voice.py for the receive side.
 *
 * Why this exists (#718): without it the server only sees the WS
 * protocol exchange; the tablet's internal state-machine logs (the
 * `[MIC DEBUG]` lines you see in the dev console) stay tablet-local.
 * #716 was only diagnosable because Bas manually pasted them. This
 * automates that path so Alpha-tester debugging doesn't need the
 * tester to share their browser console.
 *
 * Memory cap: BUFFER_CAP entries × ~200 bytes ≈ 100 KB. The ring
 * drops oldest entries when full; the `truncated` flag on flush
 * tells the admin we lost earlier entries.
 *
 * Idempotent — installing the wrapper twice is a no-op (the second
 * install detects the marker on the patched method and skips). Useful
 * for hot-reloads and `<React.StrictMode>` double-mounts.
 *
 * Failure isolation: every code path here swallows. The original
 * console methods are always called first, so even if our capture
 * misbehaves, dev-console output stays intact.
 */

const BUFFER_CAP = 500;
const INSTALLED_MARKER = '__amigomio_console_capture_installed__';

const ring = [];
let truncated = false;

function pushEntry(level, args) {
  try {
    // Stringify args defensively. Objects → JSON; circulars → toString.
    const message = Array.from(args)
      .map((a) => {
        if (a == null) return String(a);
        if (typeof a === 'string') return a;
        if (typeof a === 'number' || typeof a === 'boolean') return String(a);
        try {
          return JSON.stringify(a);
        } catch (_) {
          try { return String(a); } catch (__) { return '[unserialisable]'; }
        }
      })
      .join(' ');
    ring.push({
      ts: new Date().toISOString(),
      level,
      message,
    });
    if (ring.length > BUFFER_CAP) {
      ring.shift();
      truncated = true;
    }
  } catch (_) {
    // Capture must never throw — silent drop is the only safe failure.
  }
}

/**
 * Install the console wrappers + global error handlers. Safe to call
 * multiple times — the second call is a no-op.
 */
export function installConsoleCapture() {
  if (typeof window === 'undefined') return;
  if (window[INSTALLED_MARKER]) return;
  window[INSTALLED_MARKER] = true;

  const LEVELS = ['log', 'info', 'warn', 'error'];
  for (const level of LEVELS) {
    const original = window.console[level];
    if (typeof original !== 'function') continue;
    // The wrapped function — calls original first (so dev-tools
    // still see the message immediately), then captures.
    const wrapped = function (...args) {
      try { original.apply(window.console, args); } catch (_) { /* swallow */ }
      pushEntry(level, args);
    };
    // #1036 — remember the pre-wrap method so `_testing_reset` can fully
    // unwrap between tests. Without this, repeated install across tests
    // nests wrappers and multiplies captured entries. Production never
    // unwraps, so this is inert outside the unit tests.
    wrapped.__amigomioOriginal = original;
    window.console[level] = wrapped;
  }

  // Uncaught errors: format the basic location + stack into a single
  // log line so the buffer carries something useful even when the
  // app crashes hard.
  window.addEventListener('error', (event) => {
    const e = event.error || {};
    pushEntry('error', [
      'window.error:',
      (event.message || e.message || 'unknown'),
      event.filename ? `at ${event.filename}:${event.lineno}:${event.colno}` : '',
      e.stack || '',
    ]);
  });

  // Unhandled promise rejections — same treatment.
  window.addEventListener('unhandledrejection', (event) => {
    const r = event.reason;
    let msg;
    if (r == null) msg = 'undefined rejection';
    else if (typeof r === 'string') msg = r;
    else if (r.message) msg = r.message;
    else { try { msg = JSON.stringify(r); } catch (_) { msg = String(r); } }
    pushEntry('error', [
      'unhandledrejection:',
      msg,
      (r && r.stack) || '',
    ]);
  });
}

/**
 * Send the buffered entries over the given WS as a single
 * `client_console_logs` message. After a successful send the local
 * ring is cleared so a subsequent flush (e.g. reconnect) doesn't
 * re-send the same entries. Swallows all failures — flush is best-
 * effort.
 *
 * @param {WebSocket} ws — the active voice-call WebSocket
 * @returns {number} count of entries sent (0 if nothing or ws closed)
 */
export function flushConsoleCapture(ws) {
  if (!ws || ws.readyState !== WebSocket.OPEN) return 0;
  if (ring.length === 0) return 0;
  const entries = ring.slice();
  const was_truncated = truncated;
  try {
    ws.send(JSON.stringify({
      type: 'client_console_logs',
      entries,
      truncated: was_truncated,
    }));
    ring.length = 0;
    truncated = false;
    return entries.length;
  } catch (_) {
    // Flush is best-effort; leave the ring intact so a future
    // reconnect can retry.
    return 0;
  }
}

// Test-only access to the ring + reset (used by unit tests).
export function _testing_peek() {
  return { entries: ring.slice(), truncated };
}
export function _testing_reset() {
  ring.length = 0;
  truncated = false;
  if (typeof window !== 'undefined') {
    // #1036 — restore any wrapped console methods to their pre-wrap
    // originals so the next install wraps a clean console (single layer),
    // keeping tests isolated. Test-only; production never calls this.
    for (const level of ['log', 'info', 'warn', 'error']) {
      const cur = window.console && window.console[level];
      if (cur && cur.__amigomioOriginal) {
        window.console[level] = cur.__amigomioOriginal;
      }
    }
    delete window[INSTALLED_MARKER];
  }
}
